// BUILD 2 C2-13: complete human-readable locale catalogs; no placeholder locale suffixes.
import type { NamespaceCatalog } from '@/i18n/ns';

const keys = [
  'recognisedInMeetings',

  'title',
  'whatItIs',
  'consentTitle',
  'consentBody',
  'consentCheckbox',
  'agree',
  'recordStart',
  'passage',
  'stop',
  'tooShort',
  'multipleSpeakers',
  'poorAudio',
  'saved',
  'processing',
  'recording',
  'loading',
  'sources',
  'sourceRecording',
  'sourceMeeting',
  'removeSource',
  'delete',
  'deleteConfirm',
  'deleted',
  'orgOff',
  'unavailable',
  'staleModel',
  'usage',
  'orgTitle',
  'voicePassSwitch',
  'signaturesSwitch',
  'unusedDays',
  'purgeWarning',
  'enrolledCount',
  'auditTitle',
  'errorConsentRequired',
  'errorConsentChanged',
  'errorNotSubject',
  'errorNotEligible',
  'errorExpired',
  'errorPurgeConfirmationRequired',
] as const;
export type VoiceSignatureCopyKey = (typeof keys)[number];

const en = {
  recognisedInMeetings: 'Your voice: recognised in meetings',

  title: 'Voice signature',
  whatItIs:
    'A voice signature can name you automatically in your company’s meetings when the match is strong, or suggest your name when it is less certain. It never approves or unlocks anything.',
  consentTitle: 'Before you set up a voice signature',
  consentBody:
    'Poneva creates a voice signature from a short recording or meeting speech you confirm as your own. It is stored encrypted and compared only on Poneva’s own servers, within your company. Strong matches apply your name automatically with Undo; less certain matches suggest it for confirmation. You can delete your signature at any time, removing its open suggestions too.',
  consentCheckbox: 'I understand and want a voice signature.',
  agree: 'I agree',
  recordStart: 'Start recording',
  passage:
    'Every morning the harbour wakes slowly. Fishing boats return with the tide, gulls circle above the market stalls, and the smell of fresh bread drifts from the bakery on the corner. By nine o’clock the square is full: people buying coffee, children on their way to school, and a van unloading crates of apples and pears. Later the wind turns and clouds gather over the islands, but the café stays busy until closing time. When evening comes, the lights along the pier switch on one by one, and the last ferry of the day leaves for the northern shore.',
  stop: 'Stop',
  tooShort: 'We need a little more speech. Please read the passage again.',
  multipleSpeakers: 'We heard more than one voice. Please record somewhere quiet, on your own.',
  poorAudio: 'The recording was too quiet or too loud. Please try again.',
  saved: 'Your voice signature is saved.',
  processing: 'Creating your voice signature…',
  recording: 'Recording…',
  loading: 'Loading voice settings…',
  sources: 'Made from',
  sourceRecording: 'A recording on {date}',
  sourceMeeting: '{title} on {date}',
  removeSource: 'Remove',
  delete: 'Delete voice signature',
  deleteConfirm: 'Delete your voice signature? Its open suggestions are removed too.',
  deleted: 'Your voice signature is deleted.',
  orgOff: 'Your company has not turned on voice signatures.',
  unavailable: 'Voice signatures are not available right now.',
  staleModel:
    'Voice checks were updated. Set up your voice signature again to keep getting suggestions.',
  usage: 'Suggested your name {count} times',
  orgTitle: 'Voice in meetings',
  voicePassSwitch: 'Check voices in meetings',
  signaturesSwitch: 'Allow voice signatures',
  unusedDays: 'Delete voice signatures unused for this many days',
  purgeWarning: 'Turning this off deletes {count} voice signatures.',
  enrolledCount: 'Voice signatures: {count}',
  auditTitle: 'Voice activity',
  errorConsentRequired: 'Agree to the voice signature terms first.',
  errorConsentChanged: 'The terms changed. Read them again and agree.',
  errorNotSubject: 'You can only set up your own voice signature.',
  errorNotEligible: 'There is not enough of your own confirmed speech in this meeting.',
  errorExpired: 'This recording expired. Start again.',
  errorPurgeConfirmationRequired: 'Confirm that every voice signature will be deleted.',
};

const bg = {
  recognisedInMeetings: 'Вашият глас: разпознава се в срещи',

  title: 'Гласов подпис',
  whatItIs:
    'Гласовият подпис може автоматично да добавя името ви в срещите на вашата компания при силно съвпадение или да го предлага при по-ниска увереност. Той никога не одобрява и не отключва нищо.',
  consentTitle: 'Преди да настроите гласов подпис',
  consentBody:
    'Poneva създава гласов подпис от кратък запис или реч от среща, която потвърдите като своя. Той се съхранява криптиран и се сравнява само на сървърите на Poneva, в рамките на вашата компания. Силните съвпадения добавят името ви автоматично с възможност за отмяна; по-несигурните го предлагат за потвърждение. Можете да изтриете подписа си по всяко време, заедно с отворените му предложения.',
  consentCheckbox: 'Разбирам и искам гласов подпис.',
  agree: 'съгласен съм',
  recordStart: 'Започнете записа',
  passage:
    'Всяка сутрин пристанището се събужда бавно. Рибарски лодки се връщат с прилива, чайки кръжат над сергиите на пазара и миризмата на пресен хляб се носи от пекарната на ъгъла. Към девет часа площадът е пълен: хора, които купуват кафе, деца на път за училище и микробус, който разтоварва щайги с ябълки и круши. По-късно вятърът се обръща и облаците се събират над островите, но кафенето остава заето до затваряне. С настъпването на вечерта светлините по кея светват една по една и последният ферибот за деня тръгва към северния бряг.',
  stop: 'Спрете',
  tooShort: 'Имаме нужда от малко повече реч. Моля, прочетете пасажа отново.',
  multipleSpeakers: 'Чухме повече от един глас. Моля, записвайте на тихо място, сами.',
  poorAudio: 'Записът беше твърде тих или твърде силен. Моля, опитайте отново.',
  saved: 'Вашият гласов подпис е запазен.',
  processing: 'Вашият гласов подпис се създава...',
  recording: 'Записва се…',
  loading: 'Зареждане на настройките за глас…',
  sources: 'Изработен от',
  sourceRecording: 'Запис на {date}',
  sourceMeeting: '{title} на {date}',
  removeSource: 'Премахнете',
  delete: 'Изтриване на гласов подпис',
  deleteConfirm:
    'Изтриване на гласовия ви подпис? Неговите отворени предложения също се премахват.',
  deleted: 'Вашият гласов подпис е изтрит.',
  orgOff: 'Вашата компания не е включила гласови подписи.',
  unavailable: 'Гласовите подписи не са налични в момента.',
  staleModel:
    'Гласовите проверки бяха актуализирани. Настройте гласовия си подпис отново, за да продължите да получавате предложения.',
  usage: 'Предложи името ви {count} пъти',
  orgTitle: 'Глас в срещи',
  voicePassSwitch: 'Проверете гласовете в срещите',
  signaturesSwitch: 'Разрешаване на гласови подписи',
  unusedDays: 'Изтриване на гласови подписи, неизползвани през тези много дни',
  purgeWarning: 'Изключването на това изтрива гласовите подписи {count}.',
  enrolledCount: 'Гласови подписи: {count}',
  auditTitle: 'Гласова дейност',
  errorConsentRequired: 'Първо се съгласете с условията за гласов подпис.',
  errorConsentChanged: 'Условията се промениха. Прочетете ги отново и се съгласете.',
  errorNotSubject: 'Можете да настроите само свой собствен гласов подпис.',
  errorNotEligible: 'Няма достатъчно ваша собствена потвърдена реч в тази среща.',
  errorExpired: 'Този запис е изтекъл. Започнете отново.',
  errorPurgeConfirmationRequired: 'Потвърдете, че всеки гласов подпис ще бъде изтрит.',
};

const hr = {
  recognisedInMeetings: 'Vaš glas: prepoznaje se na sastancima',

  title: 'Glasovni potpis',
  whatItIs:
    'Glasovni potpis može automatski dodati vaše ime na sastancima vaše tvrtke kada je podudaranje snažno ili ga predložiti kada je manje sigurno. Nikada ništa ne odobrava niti otključava.',
  consentTitle: 'Prije nego što postavite glasovni potpis',
  consentBody:
    'Poneva stvara glasovni potpis iz kratke snimke ili govora sa sastanka koji potvrdite kao svoj. Pohranjuje se šifrirano i uspoređuje samo na poslužiteljima Poneve, unutar vaše tvrtke. Snažna podudaranja automatski dodaju vaše ime uz mogućnost poništavanja; manje sigurna ga predlažu za potvrdu. Potpis možete izbrisati u bilo kojem trenutku, zajedno s njegovim otvorenim prijedlozima.',
  consentCheckbox: 'Razumijem i želim glasovni potpis.',
  agree: 'slažem se',
  recordStart: 'Započnite snimanje',
  passage:
    'Svako jutro luka se polako budi. Ribarski brodovi vraćaju se s plimom, galebovi kruže iznad štandova na tržnici, a iz pekare na uglu dopire miris svježeg kruha. Do devet sati trg je pun: ljudi koji kupuju kavu, djeca na putu u školu i kombi koji istovara gajbe jabuka i krušaka. Kasnije se vjetar okreće i oblaci se skupljaju nad otocima, ali kafić ostaje zauzet do zatvaranja. Kad padne večer, svjetla duž pristaništa pale se jedno po jedno, a posljednji trajekt u danu isplovljava prema sjevernoj obali.',
  stop: 'Stop',
  tooShort: 'Trebamo još malo govora. Molimo vas da ponovno pročitate odlomak.',
  multipleSpeakers: 'Čuli smo više od jednog glasa. Molimo vas da snimate na mirnom mjestu, sami.',
  poorAudio: 'Snimka je bila pretiha ili preglasna. Molimo pokušajte ponovo.',
  saved: 'Vaš glasovni potpis je spremljen.',
  processing: 'Stvaranje vašeg glasovnog potpisa…',
  recording: 'Snimanje…',
  loading: 'Učitavanje postavki glasa…',
  sources: 'Izrađeno od',
  sourceRecording: 'Snimak na {date}',
  sourceMeeting: '{title} na {date}',
  removeSource: 'Ukloniti',
  delete: 'Izbriši glasovni potpis',
  deleteConfirm:
    'Izbrisati svoj glasovni potpis? Njegovi otvoreni prijedlozi su također uklonjeni.',
  deleted: 'Vaš je glasovni potpis izbrisan.',
  orgOff: 'Vaša tvrtka nije uključila glasovne potpise.',
  unavailable: 'Glasovni potpisi trenutno nisu dostupni.',
  staleModel:
    'Ažurirane su glasovne provjere. Ponovno postavite svoj glasovni potpis da biste nastavili dobivati ​​prijedloge.',
  usage: 'Predložio svoje ime {count} puta',
  orgTitle: 'Glas na sastancima',
  voicePassSwitch: 'Provjerite glasove na sastancima',
  signaturesSwitch: 'Dopusti glasovne potpise',
  unusedDays: 'Izbrišite glasovne potpise koji nisu korišteni toliko dana',
  purgeWarning: 'Isključivanjem ove opcije brišu se glasovni potpisi {count}.',
  enrolledCount: 'Glasovni potpisi: {count}',
  auditTitle: 'Glasovna aktivnost',
  errorConsentRequired: 'Najprije se složite s uvjetima glasovnog potpisa.',
  errorConsentChanged: 'Uvjeti su se promijenili. Pročitajte ih ponovno i složite se.',
  errorNotSubject: 'Možete postaviti samo vlastiti glasovni potpis.',
  errorNotEligible: 'Nema dovoljno vašeg vlastitog potvrđenog govora na ovom sastanku.',
  errorExpired: 'Ova snimka je istekla. Počni ponovno.',
  errorPurgeConfirmationRequired: 'Potvrdite da će svaki glasovni potpis biti izbrisan.',
};

const cs = {
  recognisedInMeetings: 'Váš hlas: rozpoznáván na schůzkách',

  title: 'Hlasový podpis',
  whatItIs:
    'Hlasový podpis vás může při silné shodě automaticky pojmenovat na schůzkách vaší firmy nebo při nižší jistotě vaše jméno navrhnout. Nikdy nic neschvaluje ani neodemyká.',
  consentTitle: 'Než nastavíte hlasový podpis',
  consentBody:
    'Poneva vytvoří hlasový podpis z krátké nahrávky nebo řeči ze schůzky, kterou potvrdíte jako vlastní. Je uložen šifrovaně a porovnává se pouze na serverech Poneva v rámci vaší firmy. Silné shody automaticky přiřadí vaše jméno s možností vrácení; méně jisté shody ho navrhnou k potvrzení. Podpis můžete kdykoli smazat spolu s jeho otevřenými návrhy.',
  consentCheckbox: 'Rozumím a chci hlasový podpis.',
  agree: 'souhlasím',
  recordStart: 'Spusťte nahrávání',
  passage:
    'Každé ráno se přístav pomalu probouzí. Rybářské lodě se vracejí s přílivem, nad stánky na trhu krouží racci a z pekárny na rohu se line vůně čerstvého chleba. V devět hodin je náměstí plné: lidé kupují kávu, děti na cestě do školy a dodávka vykládá bedny jablek a hrušek. Později se vítr otočí a nad ostrovy se stahují mraky, ale kavárna zůstává rušná až do zavírací doby. Když přijde večer, světla na molu se jedno po druhém rozsvěcují a poslední trajekt dne odjíždí na severní břeh.',
  stop: 'Zastávka',
  tooShort: 'Potřebujeme trochu více řeči. Přečtěte si prosím pasáž znovu.',
  multipleSpeakers: 'Slyšeli jsme více než jeden hlas. Nahrávejte prosím někde v klidu, sami.',
  poorAudio: 'Záznam byl příliš tichý nebo příliš hlasitý. Zkuste to prosím znovu.',
  saved: 'Váš hlasový podpis je uložen.',
  processing: 'Vytváření vašeho hlasového podpisu…',
  recording: 'Nahrávání…',
  loading: 'Načítání nastavení hlasu…',
  sources: 'Vyrobeno z',
  sourceRecording: 'Záznam na {date}',
  sourceMeeting: '{title} na {date}',
  removeSource: 'Odstranit',
  delete: 'Smazat hlasový podpis',
  deleteConfirm: 'Smazat svůj hlasový podpis? Jeho otevřené návrhy jsou také odstraněny.',
  deleted: 'Váš hlasový podpis je smazán.',
  orgOff: 'Vaše společnost nezapnula hlasové podpisy.',
  unavailable: 'Hlasové podpisy momentálně nejsou k dispozici.',
  staleModel:
    'Hlasové kontroly byly aktualizovány. Chcete-li nadále dostávat návrhy, nastavte si hlasový podpis znovu.',
  usage: 'Vaše jméno bylo navrženo {count} krát',
  orgTitle: 'Hlas na schůzkách',
  voicePassSwitch: 'Zkontrolujte hlasy na schůzkách',
  signaturesSwitch: 'Povolit hlasové podpisy',
  unusedDays: 'Smažte hlasové podpisy, které se tolik dní nepoužívaly',
  purgeWarning: 'Vypnutím této možnosti odstraníte hlasové podpisy {count}.',
  enrolledCount: 'Hlasové podpisy: {count}',
  auditTitle: 'Hlasová aktivita',
  errorConsentRequired: 'Nejprve odsouhlaste podmínky hlasového podpisu.',
  errorConsentChanged: 'Podmínky se změnily. Přečtěte si je znovu a souhlasíte.',
  errorNotSubject: 'Můžete nastavit pouze svůj vlastní hlasový podpis.',
  errorNotEligible: 'Na tomto setkání není dostatek vašich vlastních potvrzených projevů.',
  errorExpired: 'Platnost této nahrávky vypršela. Začněte znovu.',
  errorPurgeConfirmationRequired: 'Potvrďte, že každý hlasový podpis bude smazán.',
};

const da = {
  recognisedInMeetings: 'Din stemme: genkendes i møder',

  title: 'Stemmesignatur',
  whatItIs:
    'En stemmesignatur kan sætte dit navn på automatisk i virksomhedens møder ved et stærkt match eller foreslå det ved større usikkerhed. Den godkender eller låser aldrig noget op.',
  consentTitle: 'Før du opsætter en stemmesignatur',
  consentBody:
    'Poneva opretter en stemmesignatur fra en kort optagelse eller tale fra et møde, som du bekræfter er din egen. Den gemmes krypteret og sammenlignes kun på Ponevas egne servere, inden for din virksomhed. Stærke match sætter dit navn på automatisk med mulighed for at fortryde; mindre sikre match foreslår det til bekræftelse. Du kan altid slette signaturen og dens åbne forslag.',
  consentCheckbox: 'Jeg forstår og vil have en stemmesignatur.',
  agree: 'Jeg er enig',
  recordStart: 'Start optagelsen',
  passage:
    'Hver morgen vågner havnen langsomt. Fiskerbåde vender tilbage med tidevandet, måger kredser over markedsboderne, og duften af ​​frisk brød driver fra bageriet på hjørnet. Ved nitiden er pladsen fyldt: Folk køber kaffe, børn på vej til skole og en varevogn, der læsser kasser med æbler og pærer af. Senere vender vinden og skyer samler sig over øerne, men caféen holder travlt indtil lukketid. Når aftenen kommer, tændes lyset langs molen én efter én, og dagens sidste færge sejler til den nordlige kyst.',
  stop: 'Stop',
  tooShort: 'Vi har brug for lidt mere tale. Læs venligst passagen igen.',
  multipleSpeakers: 'Vi hørte mere end én stemme. Optag venligst et stille sted, på egen hånd.',
  poorAudio: 'Optagelsen var for lav eller for høj. Prøv venligst igen.',
  saved: 'Din stemmesignatur er gemt.',
  processing: 'Opretter din stemmesignatur...',
  recording: 'Optager…',
  loading: 'Indlæser stemmeindstillinger…',
  sources: 'Fremstillet af',
  sourceRecording: 'En optagelse på {date}',
  sourceMeeting: '{title} på {date}',
  removeSource: 'Fjerne',
  delete: 'Slet stemmesignatur',
  deleteConfirm: 'Vil du slette din stemmesignatur? Dens åbne forslag fjernes også.',
  deleted: 'Din stemmesignatur er slettet.',
  orgOff: 'Din virksomhed har ikke slået stemmesignaturer til.',
  unavailable: 'Stemmesignaturer er ikke tilgængelige lige nu.',
  staleModel:
    'Stemmetjek blev opdateret. Konfigurer din stemmesignatur igen for at blive ved med at få forslag.',
  usage: 'Foreslået dit navn {count} gange',
  orgTitle: 'Stemme i møder',
  voicePassSwitch: 'Tjek stemmer i møder',
  signaturesSwitch: 'Tillad stemmesignaturer',
  unusedDays: 'Slet stemmesignaturer, der ikke er brugt i så mange dage',
  purgeWarning: 'Deaktivering af dette sletter {count} stemmesignaturer.',
  enrolledCount: 'Stemmesignaturer: {count}',
  auditTitle: 'Stemmeaktivitet',
  errorConsentRequired: 'Accepter først stemmesignaturvilkårene.',
  errorConsentChanged: 'Vilkårene ændrede sig. Læs dem igen og enig.',
  errorNotSubject: 'Du kan kun opsætte din egen stemmesignatur.',
  errorNotEligible: 'Der er ikke nok af din egen bekræftede tale i dette møde.',
  errorExpired: 'Denne optagelse udløb. Start igen.',
  errorPurgeConfirmationRequired: 'Bekræft, at hver stemmesignatur vil blive slettet.',
};

const nl = {
  recognisedInMeetings: 'Je stem: wordt herkend in vergaderingen',

  title: 'Stem handtekening',
  whatItIs:
    'Een stemhandtekening kan je naam automatisch toevoegen in bedrijfsvergaderingen bij een sterke overeenkomst, of je naam voorstellen bij minder zekerheid. Ze keurt nooit iets goed en ontgrendelt niets.',
  consentTitle: 'Voordat u een stemhandtekening instelt',
  consentBody:
    'Poneva maakt een stemhandtekening van een korte opname of spraak uit een vergadering die je als jouw stem bevestigt. Ze wordt versleuteld opgeslagen en alleen op de eigen servers van Poneva vergeleken, binnen je bedrijf. Sterke overeenkomsten voegen je naam automatisch toe met de mogelijkheid dit ongedaan te maken; minder zekere overeenkomsten stellen je naam voor ter bevestiging. Je kunt de handtekening altijd verwijderen, samen met de openstaande suggesties.',
  consentCheckbox: 'Ik begrijp het en wil een stemhandtekening.',
  agree: 'Ik ben het ermee eens',
  recordStart: 'Begin met opnemen',
  passage:
    'Elke ochtend wordt de haven langzaam wakker. Vissersboten keren terug met het getij, meeuwen cirkelen boven de marktkramen en de geur van vers brood komt uit de bakkerij op de hoek. Tegen negen uur staat het plein vol: mensen die koffie kopen, kinderen op weg naar school en een busje dat kratten appels en peren uitlaadt. Later draait de wind en pakken wolken zich samen boven de eilanden, maar het café blijft tot sluitingstijd druk. Als het avond wordt, gaan de lichten langs de pier één voor één aan en vertrekt de laatste veerboot van de dag naar de noordelijke oever.',
  stop: 'Stop',
  tooShort: 'We hebben wat meer spraak nodig. Lees de passage nog eens.',
  multipleSpeakers: 'We hoorden meer dan één stem. Neem alsjeblieft ergens rustig op, alleen.',
  poorAudio: 'De opname was te zacht of te luid. Probeer het opnieuw.',
  saved: 'Uw stemhandtekening is opgeslagen.',
  processing: 'Uw stemhandtekening maken...',
  recording: 'Opnemen…',
  loading: 'Spraakinstellingen laden…',
  sources: 'Gemaakt van',
  sourceRecording: 'Een opname op {date}',
  sourceMeeting: '{title} op {date}',
  removeSource: 'Verwijderen',
  delete: 'Spraakhandtekening verwijderen',
  deleteConfirm:
    'Uw stemhandtekening verwijderen? De openstaande suggesties worden ook verwijderd.',
  deleted: 'Uw stemhandtekening is verwijderd.',
  orgOff: 'Uw bedrijf heeft stemhandtekeningen niet ingeschakeld.',
  unavailable: 'Spraakhandtekeningen zijn momenteel niet beschikbaar.',
  staleModel:
    'Stemcontroles zijn bijgewerkt. Stel uw stemhandtekening opnieuw in om suggesties te blijven ontvangen.',
  usage: 'Heeft uw naam {count} keer voorgesteld',
  orgTitle: 'Stem in vergaderingen',
  voicePassSwitch: 'Controleer stemmen in vergaderingen',
  signaturesSwitch: 'Stemhandtekeningen toestaan',
  unusedDays: 'Verwijder stemhandtekeningen die al zoveel dagen niet zijn gebruikt',
  purgeWarning: 'Als u dit uitschakelt, worden de {count}-stemhandtekeningen verwijderd.',
  enrolledCount: 'Stemsignaturen: {count}',
  auditTitle: 'Stemactiviteit',
  errorConsentRequired: 'Ga eerst akkoord met de voorwaarden voor stemhandtekeningen.',
  errorConsentChanged: 'De voorwaarden veranderden. Lees ze nog eens en ga akkoord.',
  errorNotSubject: 'U kunt alleen uw eigen stemhandtekening instellen.',
  errorNotEligible: 'Er is niet genoeg van uw eigen bevestigde toespraak in deze bijeenkomst.',
  errorExpired: 'Deze opname is verlopen. Begin opnieuw.',
  errorPurgeConfirmationRequired: 'Bevestig dat elke stemhandtekening wordt verwijderd.',
};

const et = {
  recognisedInMeetings: 'Sinu hääl: tuvastatav koosolekutel',

  title: 'Häälsignatuur',
  whatItIs:
    'Häälsignatuur võib tugeva vaste korral lisada ettevõtte koosolekutel teie nime automaatselt või ebakindlama vaste korral seda soovitada. See ei kinnita ega ava kunagi midagi.',
  consentTitle: 'Enne häälallkirja seadistamist',
  consentBody:
    'Poneva loob häälsignatuuri lühikesest salvestisest või koosolekukõnest, mille kinnitate enda omaks. Seda hoitakse krüpteeritult ja võrreldakse ainult Poneva serverites teie ettevõtte piires. Tugevad vasted lisavad nime automaatselt koos tagasivõtmise võimalusega; ebakindlamad vasted soovitavad nime kinnitamiseks. Saate signatuuri igal ajal kustutada koos selle avatud soovitustega.',
  consentCheckbox: 'Saan aru ja tahan häälallkirja.',
  agree: 'nõustun',
  recordStart: 'Alustage salvestamist',
  passage:
    'Igal hommikul ärkab sadam aeglaselt. Mõõnaga naasevad kalapaadid, turulettide kohal tiirlevad kajakad ja nurgapealsest pagariärist levib värske leiva lõhn. Kella üheksaks on plats täis: inimesed kohvi ostmas, lapsed kooliteel ning kaubik õunte ja pirnide kaste maha laadimas. Hiljem tuul pöördub ja pilved kogunevad saarte kohale, kuid kohvikus käib tegevust kuni sulgemiseni. Õhtu saabudes süttivad muuli ääres ükshaaval tuled ja päeva viimane praam sõidab põhjakaldale.',
  stop: 'Peatus',
  tooShort: 'Vajame veidi rohkem kõnet. Palun lugege lõik uuesti läbi.',
  multipleSpeakers: 'Kuulsime rohkem kui üht häält. Salvestage omaette kuskil vaikses kohas.',
  poorAudio: 'Salvestus oli liiga vaikne või liiga vali. Palun proovi uuesti.',
  saved: 'Teie häälallkiri on salvestatud.',
  processing: 'Teie häälallkirja loomine…',
  recording: 'Salvestamine…',
  loading: 'Häälesätete laadimine…',
  sources: 'Valmistatud',
  sourceRecording: 'Salvestus saidil {date}',
  sourceMeeting: '{title} saidil {date}',
  removeSource: 'Eemalda',
  delete: 'Kustuta häälallkiri',
  deleteConfirm: 'Kas kustutada oma häälallkiri? Samuti eemaldatakse selle avatud soovitused.',
  deleted: 'Teie häälallkiri on kustutatud.',
  orgOff: 'Teie ettevõte ei ole häälallkirju sisse lülitanud.',
  unavailable: 'Häälallkirjad pole praegu saadaval.',
  staleModel: 'Häälkontrolli värskendati. Soovituste saamiseks seadistage oma häälallkiri uuesti.',
  usage: 'Soovitas teie nime {count} korda',
  orgTitle: 'Hääl koosolekutel',
  voicePassSwitch: 'Kontrollige koosolekutel hääli',
  signaturesSwitch: 'Luba häälallkirjad',
  unusedDays: 'Kustutage mitu päeva kasutamata häälallkirjad',
  purgeWarning: 'Selle väljalülitamine kustutab {count} häälallkirjad.',
  enrolledCount: 'Häälsignatuurid: {count}',
  auditTitle: 'Hääletegevus',
  errorConsentRequired: 'Esmalt nõustuge häälallkirja tingimustega.',
  errorConsentChanged: 'Tingimused muutusid. Lugege need uuesti läbi ja nõustuge.',
  errorNotSubject: 'Saate seadistada ainult oma häälallkirja.',
  errorNotEligible: 'Sellel koosolekul ei piisa teie enda kinnitatud kõnest.',
  errorExpired: 'See salvestis aegus. Alusta uuesti.',
  errorPurgeConfirmationRequired: 'Kinnitage, et kõik häälallkirjad kustutatakse.',
};

const fi = {
  recognisedInMeetings: 'Äänesi: tunnistetaan kokouksissa',

  title: 'Ääniallekirjoitus',
  whatItIs:
    'Ääniallekirjoitus voi lisätä nimesi automaattisesti yrityksesi kokouksissa vahvan vastaavuuden perusteella tai ehdottaa sitä epävarmemmassa tapauksessa. Se ei koskaan hyväksy toimintoja tai avaa lukituksia.',
  consentTitle: 'Ennen kuin määrität ääniallekirjoituksen',
  consentBody:
    'Poneva luo ääniallekirjoituksen lyhyestä tallenteesta tai kokouspuheesta, jonka vahvistat omaksesi. Se tallennetaan salattuna, ja vertailu tapahtuu vain Ponevan omilla palvelimilla yrityksesi sisällä. Vahvat vastaavuudet lisäävät nimesi automaattisesti, ja voit kumota lisäyksen; epävarmemmat ehdottavat nimeä vahvistettavaksi. Voit poistaa allekirjoituksen milloin tahansa, jolloin myös sen avoimet ehdotukset poistetaan.',
  consentCheckbox: 'Ymmärrän ja haluan ääniallekirjoituksen.',
  agree: 'Olen samaa mieltä',
  recordStart: 'Aloita tallennus',
  passage:
    'Joka aamu satama herää hitaasti. Kalastusveneet palaavat vuoroveden mukana, lokit kiertävät torikojujen yläpuolella ja tuoreen leivän tuoksu leviää kulman leipomosta. Kello yhdeksän aikaan aukio on täynnä: kahvia ostavia ihmisiä, koulumatkalla olevia lapsia ja pakettiauto purkamassa omenoita ja päärynöitä. Myöhemmin tuuli kääntyy ja pilvet kerääntyvät saarten ylle, mutta kahvila pysyy ahkerana sulkemisaikaan asti. Illan tullessa laiturin valot syttyvät yksi kerrallaan ja päivän viimeinen lautta lähtee pohjoisrannalle.',
  stop: 'Stop',
  tooShort: 'Tarvitsemme hieman enemmän puhetta. Ole hyvä ja lue kohta uudelleen.',
  multipleSpeakers:
    'Kuulimme enemmän kuin yhden äänen. Ole hyvä ja nauhoita jossain hiljaisessa paikassa yksin.',
  poorAudio: 'Tallennus oli liian hiljainen tai liian kova. Yritä uudelleen.',
  saved: 'Ääniallekirjoituksesi on tallennettu.',
  processing: 'Luodaan ääniallekirjoitusta…',
  recording: 'Tallennetaan…',
  loading: 'Ladataan ääniasetuksia…',
  sources: 'Valmistettu',
  sourceRecording: 'Tallenne {date}:llä',
  sourceMeeting: '{title} {date}:ssä',
  removeSource: 'Poistaa',
  delete: 'Poista ääniallekirjoitus',
  deleteConfirm: 'Poistetaanko ääniallekirjoituksesi? Myös sen avoimet ehdotukset poistetaan.',
  deleted: 'Ääniallekirjoituksesi on poistettu.',
  orgOff: 'Yrityksesi ei ole ottanut ääniallekirjoituksia käyttöön.',
  unavailable: 'Ääniallekirjoitukset eivät ole käytettävissä juuri nyt.',
  staleModel:
    'Äänitarkistukset päivitettiin. Määritä ääniallekirjoitus uudelleen saadaksesi ehdotuksia.',
  usage: 'Ehdotti nimeäsi {count} kertaa',
  orgTitle: 'Ääni kokouksissa',
  voicePassSwitch: 'Tarkista äänet kokouksissa',
  signaturesSwitch: 'Salli ääniallekirjoitukset',
  unusedDays: 'Poista näin monta päivää käyttämättömät ääniallekirjoitukset',
  purgeWarning: 'Tämän poistaminen poistaa {count}-ääniallekirjoitukset.',
  enrolledCount: 'Ääniallekirjoitukset: {count}',
  auditTitle: 'Äänitoiminta',
  errorConsentRequired: 'Hyväksy ensin ääniallekirjoituksen ehdot.',
  errorConsentChanged: 'Ehdot muuttuivat. Lue ne uudelleen ja hyväksy.',
  errorNotSubject: 'Voit määrittää vain oman ääniallekirjoituksen.',
  errorNotEligible: 'Tässä kokouksessa ei ole tarpeeksi omaa vahvistettua puhettasi.',
  errorExpired: 'Tämä tallenne on vanhentunut. Aloita uudelleen.',
  errorPurgeConfirmationRequired: 'Vahvista, että jokainen ääniallekirjoitus poistetaan.',
};

const fr = {
  recognisedInMeetings: 'Votre voix : reconnue en réunion',

  title: 'Signature vocale',
  whatItIs:
    'Une signature vocale peut vous nommer automatiquement dans les réunions de votre entreprise si la correspondance est forte, ou proposer votre nom si elle est moins certaine. Elle ne valide ni ne déverrouille jamais rien.',
  consentTitle: 'Avant de configurer une signature vocale',
  consentBody:
    'Poneva crée une signature vocale à partir d’un court enregistrement ou de paroles en réunion que vous confirmez comme étant les vôtres. Elle est stockée chiffrée et comparée uniquement sur les serveurs de Poneva, au sein de votre entreprise. Les correspondances fortes appliquent votre nom automatiquement, avec possibilité d’annuler ; les moins certaines le proposent pour confirmation. Vous pouvez supprimer votre signature à tout moment, ainsi que ses suggestions en attente.',
  consentCheckbox: 'Je comprends et je veux une signature vocale.',
  agree: "Je suis d'accord",
  recordStart: "Commencer l'enregistrement",
  passage:
    "Chaque matin, le port se réveille lentement. Les bateaux de pêche reviennent avec la marée, les mouettes tournent au-dessus des étals du marché et l'odeur du pain frais s'échappe de la boulangerie du coin. Vers neuf heures, la place est pleine : des gens qui achètent du café, des enfants qui se rendent à l’école et une camionnette déchargeant des caisses de pommes et de poires. Plus tard, le vent tourne et les nuages ​​s'amoncellent sur les îles, mais le café reste occupé jusqu'à la fermeture. Le soir venu, les lumières du quai s'allument une à une et le dernier ferry de la journée part pour la rive nord.",
  stop: 'Arrêt',
  tooShort: "Nous avons besoin d'un peu plus de discours. Veuillez relire le passage.",
  multipleSpeakers:
    "Nous avons entendu plus d'une voix. Veuillez enregistrer dans un endroit calme, seul.",
  poorAudio: "L'enregistrement était trop faible ou trop fort. Veuillez réessayer.",
  saved: 'Votre signature vocale est enregistrée.',
  processing: 'Création de votre signature vocale…',
  recording: 'Enregistrement…',
  loading: 'Chargement des paramètres vocaux…',
  sources: 'Fabriqué à partir de',
  sourceRecording: 'Un enregistrement sur {date}',
  sourceMeeting: '{title} sur {date}',
  removeSource: 'Retirer',
  delete: 'Supprimer la signature vocale',
  deleteConfirm:
    'Supprimer votre signature vocale ? Ses suggestions ouvertes sont également supprimées.',
  deleted: 'Votre signature vocale est supprimée.',
  orgOff: "Votre entreprise n'a pas activé les signatures vocales.",
  unavailable: 'Les signatures vocales ne sont pas disponibles pour le moment.',
  staleModel:
    'Les contrôles vocaux ont été mis à jour. Configurez à nouveau votre signature vocale pour continuer à recevoir des suggestions.',
  usage: "J'ai suggéré votre nom {count} fois",
  orgTitle: 'Voix dans les réunions',
  voicePassSwitch: 'Vérifier les voix lors des réunions',
  signaturesSwitch: 'Autoriser les signatures vocales',
  unusedDays: 'Supprimer les signatures vocales inutilisées pendant autant de jours',
  purgeWarning: 'La désactivation de cette option supprime les signatures vocales {count}.',
  enrolledCount: 'Signatures vocales: {count}',
  auditTitle: 'Activité vocale',
  errorConsentRequired: 'Acceptez d’abord les termes de la signature vocale.',
  errorConsentChanged: 'Les termes ont changé. Relisez-les et acceptez.',
  errorNotSubject: 'Vous ne pouvez configurer que votre propre signature vocale.',
  errorNotEligible: "Il n'y a pas assez de votre propre discours confirmé dans cette réunion.",
  errorExpired: 'Cet enregistrement a expiré. Recommencer.',
  errorPurgeConfirmationRequired: 'Confirmez que chaque signature vocale sera supprimée.',
};

const de = {
  recognisedInMeetings: 'Ihre Stimme: wird in Besprechungen erkannt',

  title: 'Sprachsignatur',
  whatItIs:
    'Eine Stimmensignatur kann bei einer starken Übereinstimmung Ihren Namen in Firmenbesprechungen automatisch zuordnen oder ihn bei geringerer Sicherheit vorschlagen. Sie genehmigt und entsperrt niemals etwas.',
  consentTitle: 'Bevor Sie eine Sprachsignatur einrichten',
  consentBody:
    'Poneva erstellt eine Stimmensignatur aus einer kurzen Aufnahme oder aus Besprechungsbeiträgen, die Sie als Ihre eigenen bestätigen. Sie wird verschlüsselt gespeichert und nur auf Ponevas eigenen Servern innerhalb Ihres Unternehmens verglichen. Starke Übereinstimmungen ordnen Ihren Namen automatisch zu und lassen sich rückgängig machen; weniger sichere schlagen ihn zur Bestätigung vor. Sie können die Signatur jederzeit samt ihren offenen Vorschlägen löschen.',
  consentCheckbox: 'Ich verstehe und möchte eine Sprachsignatur.',
  agree: 'Ich stimme zu',
  recordStart: 'Starten Sie die Aufnahme',
  passage:
    'Jeden Morgen erwacht der Hafen langsam. Fischerboote kehren mit der Flut zurück, Möwen kreisen über den Marktständen und aus der Bäckerei an der Ecke weht der Duft von frischem Brot. Um neun Uhr ist der Platz voll: Leute, die Kaffee kaufen, Kinder auf dem Weg zur Schule und ein Lieferwagen, der Kisten mit Äpfeln und Birnen auslädt. Später dreht der Wind und Wolken ziehen über den Inseln zusammen, aber das Café bleibt bis zur Schließung gut besucht. Wenn es Abend wird, schalten sich nach und nach die Lichter am Pier ein und die letzte Fähre des Tages legt zum Nordufer ab.',
  stop: 'Stoppen',
  tooShort: 'Wir brauchen etwas mehr Rede. Bitte lesen Sie die Passage noch einmal.',
  multipleSpeakers:
    'Wir hörten mehr als eine Stimme. Bitte nehmen Sie die Aufnahmen alleine an einem ruhigen Ort auf.',
  poorAudio: 'Die Aufnahme war zu leise oder zu laut. Bitte versuchen Sie es erneut.',
  saved: 'Ihre Sprachsignatur wird gespeichert.',
  processing: 'Erstellen Sie Ihre Sprachsignatur…',
  recording: 'Aufnahme läuft…',
  loading: 'Spracheinstellungen werden geladen…',
  sources: 'Hergestellt aus',
  sourceRecording: 'Eine Aufnahme auf {date}',
  sourceMeeting: '{title} auf {date}',
  removeSource: 'Entfernen',
  delete: 'Sprachsignatur löschen',
  deleteConfirm: 'Sprachsignatur löschen? Die offenen Vorschläge werden ebenfalls entfernt.',
  deleted: 'Ihre Sprachsignatur wird gelöscht.',
  orgOff: 'Ihr Unternehmen hat Sprachsignaturen nicht aktiviert.',
  unavailable: 'Sprachsignaturen sind derzeit nicht verfügbar.',
  staleModel:
    'Sprachkontrollen wurden aktualisiert. Richten Sie Ihre Sprachsignatur erneut ein, um weiterhin Vorschläge zu erhalten.',
  usage: 'Ihr Name wurde {count}-mal vorgeschlagen',
  orgTitle: 'Stimme in Meetings',
  voicePassSwitch: 'Überprüfen Sie Stimmen in Besprechungen',
  signaturesSwitch: 'Sprachsignaturen zulassen',
  unusedDays: 'Löschen Sie Sprachsignaturen, die für diese viele Tage nicht verwendet wurden',
  purgeWarning: 'Wenn Sie dies deaktivieren, werden {count}-Sprachsignaturen gelöscht.',
  enrolledCount: 'Stimmsignaturen: {count}',
  auditTitle: 'Sprachaktivität',
  errorConsentRequired: 'Stimmen Sie zunächst den Bedingungen für die Sprachsignatur zu.',
  errorConsentChanged:
    'Die Bedingungen haben sich geändert. Lesen Sie sie noch einmal und stimmen Sie zu.',
  errorNotSubject: 'Sie können nur Ihre eigene Sprachsignatur einrichten.',
  errorNotEligible: 'Von Ihrer eigenen bestätigten Rede in diesem Treffen gibt es nicht genug.',
  errorExpired: 'Diese Aufnahme ist abgelaufen. Beginnen Sie erneut.',
  errorPurgeConfirmationRequired: 'Bestätigen Sie, dass jede Sprachsignatur gelöscht wird.',
};

const el = {
  recognisedInMeetings: 'Η φωνή σας: αναγνωρίζεται στις συναντήσεις',

  title: 'Φωνητική υπογραφή',
  whatItIs:
    'Η φωνητική υπογραφή μπορεί να σας ονομάζει αυτόματα στις συναντήσεις της εταιρείας σας όταν η αντιστοίχιση είναι ισχυρή ή να προτείνει το όνομά σας όταν είναι λιγότερο βέβαιη. Δεν εγκρίνει ούτε ξεκλειδώνει ποτέ τίποτα.',
  consentTitle: 'Πριν ρυθμίσετε μια φωνητική υπογραφή',
  consentBody:
    'Η Poneva δημιουργεί φωνητική υπογραφή από σύντομη εγγραφή ή ομιλία σε συνάντηση που επιβεβαιώνετε ως δική σας. Αποθηκεύεται κρυπτογραφημένη και συγκρίνεται μόνο στους διακομιστές της Poneva, εντός της εταιρείας σας. Οι ισχυρές αντιστοιχίσεις εφαρμόζουν αυτόματα το όνομά σας με δυνατότητα αναίρεσης· οι λιγότερο βέβαιες το προτείνουν για επιβεβαίωση. Μπορείτε να διαγράψετε την υπογραφή σας οποτεδήποτε, μαζί με τις ανοιχτές προτάσεις της.',
  consentCheckbox: 'Καταλαβαίνω και θέλω φωνητική υπογραφή.',
  agree: 'συμφωνώ',
  recordStart: 'Ξεκινήστε την εγγραφή',
  passage:
    'Κάθε πρωί το λιμάνι ξυπνάει αργά. Τα ψαροκάικα επιστρέφουν με την παλίρροια, οι γλάροι κάνουν κύκλους πάνω από τους πάγκους της αγοράς και η μυρωδιά φρέσκου ψωμιού παρασύρεται από το αρτοποιείο στη γωνία. Στις εννιά η πλατεία είναι γεμάτη: κόσμος που αγοράζει καφέ, παιδιά στο δρόμο για το σχολείο και ένα φορτηγάκι που ξεφορτώνει τελάρα με μήλα και αχλάδια. Αργότερα ο αέρας γυρίζει και τα σύννεφα μαζεύονται πάνω από τα νησιά, αλλά το καφέ παραμένει απασχολημένο μέχρι το κλείσιμο. Όταν βραδιάζει, τα φώτα κατά μήκος της προβλήτας ανάβουν ένα-ένα και το τελευταίο πλοίο της ημέρας φεύγει για τη βόρεια ακτή.',
  stop: 'Στάση',
  tooShort: 'Χρειαζόμαστε λίγο ακόμα λόγο. Διαβάστε ξανά το απόσπασμα.',
  multipleSpeakers:
    'Ακούσαμε περισσότερες από μία φωνές. Παρακαλώ ηχογραφήστε κάπου ήσυχα, μόνοι σας.',
  poorAudio: 'Η ηχογράφηση ήταν πολύ αθόρυβη ή πολύ δυνατή. Δοκιμάστε ξανά.',
  saved: 'Η φωνητική υπογραφή σας έχει αποθηκευτεί.',
  processing: 'Δημιουργία φωνητικής υπογραφής…',
  recording: 'Εγγραφή…',
  loading: 'Φόρτωση ρυθμίσεων φωνής…',
  sources: 'Κατασκευασμένο από',
  sourceRecording: 'Μια εγγραφή στο {date}',
  sourceMeeting: '{title} σε {date}',
  removeSource: 'Αφαιρώ',
  delete: 'Διαγραφή φωνητικής υπογραφής',
  deleteConfirm:
    'Διαγραφή της φωνητικής υπογραφής σας; Καταργούνται και οι ανοιχτές προτάσεις του.',
  deleted: 'Η φωνητική υπογραφή σας έχει διαγραφεί.',
  orgOff: 'Η εταιρεία σας δεν έχει ενεργοποιήσει τις φωνητικές υπογραφές.',
  unavailable: 'Οι φωνητικές υπογραφές δεν είναι διαθέσιμες αυτήν τη στιγμή.',
  staleModel:
    'Οι φωνητικοί έλεγχοι ενημερώθηκαν. Ρυθμίστε ξανά τη φωνητική υπογραφή σας για να συνεχίσετε να λαμβάνετε προτάσεις.',
  usage: 'Πρότεινε το όνομά σας {count} φορές',
  orgTitle: 'Φωνή στις συναντήσεις',
  voicePassSwitch: 'Ελέγξτε τις φωνές στις συναντήσεις',
  signaturesSwitch: 'Επιτρέψτε τις φωνητικές υπογραφές',
  unusedDays: 'Διαγράψτε τις φωνητικές υπογραφές που δεν χρησιμοποιήθηκαν για τόσες μέρες',
  purgeWarning: 'Η απενεργοποίηση αυτής της επιλογής διαγράφει τις φωνητικές υπογραφές {count}.',
  enrolledCount: 'Φωνητικές υπογραφές: {count}',
  auditTitle: 'Φωνητική δραστηριότητα',
  errorConsentRequired: 'Συμφωνήστε πρώτα με τους όρους φωνητικής υπογραφής.',
  errorConsentChanged: 'Οι όροι άλλαξαν. Διαβάστε τα ξανά και συμφωνήστε.',
  errorNotSubject: 'Μπορείτε να δημιουργήσετε μόνο τη δική σας φωνητική υπογραφή.',
  errorNotEligible: 'Δεν υπάρχει αρκετή δική σας επιβεβαιωμένη ομιλία σε αυτήν τη συνάντηση.',
  errorExpired: 'Αυτή η ηχογράφηση έληξε. Ξεκινήστε ξανά.',
  errorPurgeConfirmationRequired: 'Επιβεβαιώστε ότι κάθε φωνητική υπογραφή θα διαγραφεί.',
};

const hu = {
  recognisedInMeetings: 'Az Ön hangja: felismerhető az értekezleteken',

  title: 'Hangaláírás',
  whatItIs:
    'A hangaláírás erős egyezés esetén automatikusan hozzárendelheti a nevét a céges megbeszéléseken, bizonytalanabb egyezésnél pedig javasolhatja azt. Soha nem hagy jóvá és nem old fel semmit.',
  consentTitle: 'Mielőtt beállítaná a hangaláírást',
  consentBody:
    'A Poneva rövid felvételből vagy olyan megbeszélésen elhangzott beszédből készít hangaláírást, amelyet sajátjaként megerősít. Titkosítva tárolja, és csak a Poneva saját szerverein, a cégén belül hasonlítja össze. Az erős egyezések automatikusan hozzárendelik a nevét, visszavonási lehetőséggel; a bizonytalanabbak megerősítésre javasolják. A hangaláírást bármikor törölheti a nyitott javaslataival együtt.',
  consentCheckbox: 'Értem, és hangos aláírást kérek.',
  agree: 'egyetértek',
  recordStart: 'Indítsa el a felvételt',
  passage:
    'Minden reggel lassan ébred a kikötő. Az árral horgászcsónakok térnek vissza, sirályok köröznek a piaci bódék felett, a sarkon lévő pékségből pedig friss kenyér illata száll ki. Kilenc órára megtelt a tér: kávét vásárló emberek, iskolába tartó gyerekek, alma- és körtékrekeszeket kipakoló furgon. Később megfordul a szél és felhők gyülekeznek a szigetek felett, de a kávézó zárásig zsúfolt marad. Estekor a mólón sorra felkapcsolnak a lámpák, és a nap utolsó kompja az északi partra indul.',
  stop: 'Stop',
  tooShort: 'Még egy kis beszédre van szükségünk. Kérjük, olvassa el újra a részt.',
  multipleSpeakers: 'Több hangot is hallottunk. Kérlek, készíts egy csendes helyre, egyedül.',
  poorAudio: 'A felvétel túl halk vagy túl hangos volt. Kérjük, próbálja újra.',
  saved: 'A hangaláírás mentésre kerül.',
  processing: 'Hangaláírás létrehozása…',
  recording: 'Felvétel…',
  loading: 'Hangbeállítások betöltése…',
  sources: 'Készült',
  sourceRecording: 'Felvétel a {date}-n',
  sourceMeeting: '{title} a {date}-n',
  removeSource: 'Távolítsa el',
  delete: 'Törölje a hangaláírást',
  deleteConfirm: 'Törli a hangaláírását? A nyitott javaslatai is eltávolítva.',
  deleted: 'A hangaláírása törlődik.',
  orgOff: 'Cége nem kapcsolta be a hangaláírásokat.',
  unavailable: 'A hangaláírások jelenleg nem érhetők el.',
  staleModel:
    'A hangellenőrzések frissítve. Állítsa be újra hangaláírását, hogy továbbra is javaslatokat kapjon.',
  usage: 'Javasolt a neved {count} alkalommal',
  orgTitle: 'Hang az üléseken',
  voicePassSwitch: 'Hangok ellenőrzése az értekezleteken',
  signaturesSwitch: 'Hangaláírás engedélyezése',
  unusedDays: 'Törölje a sok napig nem használt hangaláírásokat',
  purgeWarning: 'Ennek kikapcsolása törli a {count} hangaláírásokat.',
  enrolledCount: 'Hangaláírások: {count}',
  auditTitle: 'Hangtevékenység',
  errorConsentRequired: 'Először fogadja el a hangaláírás feltételeit.',
  errorConsentChanged: 'A feltételek megváltoztak. Olvassa el őket újra, és ért egyet.',
  errorNotSubject: 'Csak saját hangaláírást állíthat be.',
  errorNotEligible: 'Ezen az ülésen nincs elég saját megerősített beszéde.',
  errorExpired: 'Ez a felvétel lejárt. Kezdje újra.',
  errorPurgeConfirmationRequired: 'Erősítse meg, hogy minden hangaláírás törlődik.',
};

const ga = {
  recognisedInMeetings: 'Do ghuth: aithnítear i gcruinnithe é',

  title: 'Síniú gutha',
  whatItIs:
    'Is féidir le síniú gutha d’ainm a chur i bhfeidhm go huathoibríoch i gcruinnithe do chuideachta nuair atá an mheaitseáil láidir, nó é a mholadh nuair atá sí níos neamhchinnte. Ní thugann sé cead ná ní dhíghlasálann sé aon rud riamh.',
  consentTitle: 'Sula socraíonn tú síniú gutha',
  consentBody:
    'Cruthaíonn Poneva síniú gutha ó thaifeadadh gairid nó ó chaint i gcruinniú a dheimhníonn tú mar do chuid féin. Stóráiltear é criptithe agus déantar comparáid air ar fhreastalaithe Poneva amháin, laistigh de do chuideachta. Cuireann meaitseálacha láidre d’ainm i bhfeidhm go huathoibríoch, le rogha é a chealú; molann meaitseálacha níos neamhchinnte é lena dheimhniú. Is féidir leat do shíniú agus a mholtaí oscailte a scriosadh am ar bith.',
  consentCheckbox: 'Tuigim agus ba mhaith liom síniú gutha.',
  agree: 'aontaím',
  recordStart: 'Tosaigh taifeadadh',
  passage:
    'Gach maidin dúisíonn an cuan go mall. Filleann báid iascaireachta leis an taoide, ciorcail faoileáin os cionn na stallaí margaidh, agus boladh an aráin úir ag sileadh ón mbácús ar an gcúinne. Faoi naoi a chlog tá an chearnóg lán: daoine ag ceannach caife, páistí ar a mbealach chun na scoile, agus veain ag díluchtú cliathbhoscaí úlla agus piorraí. Níos déanaí casann an ghaoth agus bailíonn scamaill thar na hoileáin, ach fanann an caifé gnóthach go dtí an t-am dúnta. Nuair a thagann an tráthnóna, lascraíonn na soilse feadh an ché ar cheann ar cheann, agus fágann bád farantóireachta deireanach an lae don chladach thuaidh.',
  stop: 'Stop',
  tooShort: 'Teastaíonn beagán níos mó cainte uainn. Léigh an sliocht arís le do thoil.',
  multipleSpeakers:
    'Chuala muid níos mó ná aon ghuth amháin. Déan taifead áit éigin ciúin, leat féin.',
  poorAudio: 'Bhí an taifeadadh ró-chiúin nó ró-ard. Bain triail eile as.',
  saved: 'Tá do shíniú gutha sábháilte.',
  processing: 'do shíniú gutha á chruthú…',
  recording: 'Ag taifeadadh…',
  loading: 'Socruithe gutha á lódáil…',
  sources: 'Déanta as',
  sourceRecording: 'Taifeadadh ar {date}',
  sourceMeeting: '{title} ar {date}',
  removeSource: 'Bain',
  delete: 'Scrios síniú gutha',
  deleteConfirm:
    'An bhfuil fonn ort do shíniú gutha a scriosadh? Baintear a moltaí oscailte freisin.',
  deleted: 'Tá do shíniú gutha scriosta.',
  orgOff: 'Níor chuir do chuideachta sínithe gutha ar siúl.',
  unavailable: 'Níl sínithe gutha ar fáil faoi láthair.',
  staleModel:
    'Nuashonraíodh seiceálacha gutha. Socraigh do shíniú gutha arís chun leanúint ar aghaidh ag fáil moltaí.',
  usage: "Moltar d'ainm {count} uaireanta",
  orgTitle: 'Guth i gcruinnithe',
  voicePassSwitch: 'Seiceáil guthanna i gcruinnithe',
  signaturesSwitch: 'Ceadaigh sínithe gutha',
  unusedDays: 'Scrios sínithe gutha nár úsáideadh le fada an lá seo',
  purgeWarning: 'Má mhúchtar é seo, scriostar sínithe gutha {count}.',
  enrolledCount: 'Sínithe gutha: {count}',
  auditTitle: 'Gníomhaíocht gutha',
  errorConsentRequired: 'Aontaigh ar dtús leis na téarmaí guthashínithe.',
  errorConsentChanged: 'D’athraigh na téarmaí. Léigh arís iad agus aontaigh.',
  errorNotSubject: 'Ní féidir leat ach do shíniú gutha féin a shocrú.',
  errorNotEligible: 'Ní leor do chuid cainte dearbhaithe féin sa chruinniú seo.',
  errorExpired: 'Chuaigh an taifeadadh seo in éag. Tosaigh arís.',
  errorPurgeConfirmationRequired: 'Deimhnigh go scriosfar gach síniú gutha.',
};

const it = {
  recognisedInMeetings: 'La tua voce: riconosciuta nelle riunioni',

  title: 'Firma vocale',
  whatItIs:
    'Una firma vocale può assegnare automaticamente il tuo nome nelle riunioni aziendali se la corrispondenza è forte, oppure suggerirlo se è meno certa. Non approva e non sblocca mai nulla.',
  consentTitle: 'Prima di impostare una firma vocale',
  consentBody:
    'Poneva crea una firma vocale da una breve registrazione o da interventi in riunione che confermi come tuoi. Viene conservata cifrata e confrontata solo sui server di Poneva, all’interno della tua azienda. Le corrispondenze forti assegnano il tuo nome automaticamente, con possibilità di annullare; quelle meno certe lo suggeriscono per la conferma. Puoi eliminare la firma in qualsiasi momento, insieme ai suggerimenti ancora aperti.',
  consentCheckbox: 'Capisco e desidero una firma vocale.',
  agree: "Sono d'accordo",
  recordStart: 'Inizia a registrare',
  passage:
    "Ogni mattina il porto si sveglia lentamente. I pescherecci ritornano con la marea, i gabbiani volteggiano sopra le bancarelle del mercato e il profumo del pane fresco arriva dal panificio all'angolo. Alle nove la piazza è piena: gente che compra il caffè, bambini che vanno a scuola e un furgone che scarica casse di mele e pere. Più tardi il vento cambia e le nuvole si addensano sulle isole, ma il bar resta occupato fino all'ora di chiusura. Quando arriva la sera, le luci lungo il molo si accendono una dopo l'altra e l'ultimo traghetto della giornata parte per la sponda settentrionale.",
  stop: 'Fermare',
  tooShort: "Abbiamo bisogno di un po' più di discorso. Per favore rileggi il passaggio.",
  multipleSpeakers:
    'Abbiamo sentito più di una voce. Per favore, registra in un posto tranquillo, da solo.',
  poorAudio: 'La registrazione era troppo bassa o troppo alta. Per favore riprova.',
  saved: 'La tua firma vocale viene salvata.',
  processing: 'Creazione della firma vocale…',
  recording: 'Registrazione…',
  loading: 'Caricamento delle impostazioni vocali…',
  sources: 'Fatto da',
  sourceRecording: 'Una registrazione su {date}',
  sourceMeeting: '{title} su {date}',
  removeSource: 'Rimuovere',
  delete: 'Elimina la firma vocale',
  deleteConfirm:
    'Vuoi eliminare la tua firma vocale? Anche i suoi suggerimenti aperti vengono rimossi.',
  deleted: 'La tua firma vocale viene eliminata.',
  orgOff: 'La tua azienda non ha attivato le firme vocali.',
  unavailable: 'Le firme vocali non sono disponibili al momento.',
  staleModel:
    'I controlli vocali sono stati aggiornati. Configura nuovamente la tua firma vocale per continuare a ricevere suggerimenti.',
  usage: 'Hai suggerito il tuo nome {count} volte',
  orgTitle: 'Voce nelle riunioni',
  voicePassSwitch: 'Controlla le voci nelle riunioni',
  signaturesSwitch: 'Consenti firme vocali',
  unusedDays: 'Elimina le firme vocali non utilizzate per questo numero di giorni',
  purgeWarning: 'Disattivando questa opzione si eliminano le firme vocali {count}.',
  enrolledCount: 'Firme vocali: {count}',
  auditTitle: 'Attività vocale',
  errorConsentRequired: 'Accettare prima i termini della firma vocale.',
  errorConsentChanged: 'I termini sono cambiati. Leggili di nuovo e accetta.',
  errorNotSubject: 'Puoi impostare solo la tua firma vocale.',
  errorNotEligible: "Non c'è abbastanza del tuo discorso confermato in questo incontro.",
  errorExpired: 'Questa registrazione è scaduta. Ricominciare.',
  errorPurgeConfirmationRequired: 'Conferma che ogni firma vocale verrà eliminata.',
};

const lv = {
  recognisedInMeetings: 'Jūsu balss: tiek atpazīta sapulcēs',

  title: 'Balss paraksts',
  whatItIs:
    'Balss paraksts var automātiski pievienot jūsu vārdu uzņēmuma sapulcēs, ja atbilstība ir spēcīga, vai ieteikt to, ja pārliecība ir mazāka. Tas nekad neko neapstiprina un neatbloķē.',
  consentTitle: 'Pirms balss paraksta iestatīšanas',
  consentBody:
    'Poneva izveido balss parakstu no īsa ieraksta vai sapulces runas, kuru apstiprināt kā savu. Tas tiek glabāts šifrēti un salīdzināts tikai Poneva serveros jūsu uzņēmuma ietvaros. Spēcīgas atbilstības automātiski pievieno jūsu vārdu ar iespēju atsaukt; mazāk pārliecinošas to iesaka apstiprināšanai. Parakstu jebkurā laikā varat izdzēst kopā ar tā atvērtajiem ieteikumiem.',
  consentCheckbox: 'Es saprotu un vēlos balss parakstu.',
  agree: 'piekrītu',
  recordStart: 'Sāciet ierakstīšanu',
  passage:
    'Katru rītu osta lēnām mostas. Līdz ar paisumu atgriežas zvejas laivas, virs tirgus stendiem riņķo kaijas, un no maiznīcas uz stūra plūst svaigas maizes smarža. Līdz pulksten deviņiem laukums ir pilns: cilvēki pērk kafiju, bērni ceļā uz skolu un furgons, kas izkrauj kastes ar āboliem un bumbieriem. Vēlāk vējš iegriežas un pār salām pulcējas mākoņi, bet kafejnīca ir rosīga līdz slēgšanas laikam. Pienākot vakaram, piestātnē pa vienam iedegas gaismas, un dienas pēdējais prāmis atiet uz ziemeļu krastu.',
  stop: 'Stop',
  tooShort: 'Mums vajag mazliet vairāk runas. Lūdzu, izlasiet fragmentu vēlreiz.',
  multipleSpeakers:
    'Mēs dzirdējām vairāk nekā vienu balsi. Lūdzu, ierakstiet kaut kur klusā vietā.',
  poorAudio: 'Ieraksts bija pārāk kluss vai pārāk skaļš. Lūdzu, mēģiniet vēlreiz.',
  saved: 'Jūsu balss paraksts ir saglabāts.',
  processing: 'Notiek jūsu balss paraksta izveide…',
  recording: 'Ierakstīšana…',
  loading: 'Ielādē balss iestatījumus…',
  sources: 'Izgatavots no',
  sourceRecording: 'Ieraksts vietnē {date}',
  sourceMeeting: '{title} vietnē {date}',
  removeSource: 'Noņemt',
  delete: 'Dzēst balss parakstu',
  deleteConfirm: 'Vai dzēst jūsu balss parakstu? Tiek noņemti arī tā atvērtie ieteikumi.',
  deleted: 'Jūsu balss paraksts ir izdzēsts.',
  orgOff: 'Jūsu uzņēmums nav ieslēdzis balss parakstus.',
  unavailable: 'Balss paraksti pašlaik nav pieejami.',
  staleModel:
    'Balss pārbaudes tika atjauninātas. Vēlreiz iestatiet balss parakstu, lai turpinātu saņemt ieteikumus.',
  usage: 'Jūsu vārds tika ieteikts {count} reizes',
  orgTitle: 'Balss sanāksmēs',
  voicePassSwitch: 'Pārbaudiet balsis sapulcēs',
  signaturesSwitch: 'Atļaut balss parakstus',
  unusedDays: 'Dzēsiet balss parakstus, kas nav izmantoti šīs daudzas dienas',
  purgeWarning: 'Izslēdzot šo funkciju, tiek dzēsti {count} balss paraksti.',
  enrolledCount: 'Balss paraksti: {count}',
  auditTitle: 'Balss darbība',
  errorConsentRequired: 'Vispirms piekrītiet balss paraksta noteikumiem.',
  errorConsentChanged: 'Noteikumi mainījās. Izlasi tos vēlreiz un piekrīti.',
  errorNotSubject: 'Varat iestatīt tikai savu balss parakstu.',
  errorNotEligible: 'Šajā sanāksmē nepietiek ar jūsu pašu apstiprināto runu.',
  errorExpired: 'Šim ierakstam beidzās derīguma termiņš. Sāciet no jauna.',
  errorPurgeConfirmationRequired: 'Apstipriniet, ka visi balss paraksti tiks dzēsti.',
};

const lt = {
  recognisedInMeetings: 'Jūsų balsas: atpažįstamas susitikimuose',

  title: 'Balso parašas',
  whatItIs:
    'Balso parašas gali automatiškai priskirti jūsų vardą įmonės susitikimuose, kai atitiktis stipri, arba pasiūlyti jį, kai ji mažiau patikima. Jis niekada nieko nepatvirtina ir neatrakina.',
  consentTitle: 'Prieš nustatydami balso parašą',
  consentBody:
    'Poneva sukuria balso parašą iš trumpo įrašo arba susitikimo kalbos, kurią patvirtinate kaip savo. Jis saugomas užšifruotas ir lyginamas tik Poneva serveriuose, jūsų įmonės ribose. Stiprios atitiktys automatiškai priskiria jūsų vardą su galimybe atšaukti; mažiau patikimos jį pasiūlo patvirtinti. Parašą galite bet kada ištrinti kartu su jo nepatvirtintais pasiūlymais.',
  consentCheckbox: 'Suprantu ir noriu balso parašo.',
  agree: 'sutinku',
  recordStart: 'Pradėkite įrašymą',
  passage:
    'Kiekvieną rytą uostas lėtai bunda. Žvejybos laivai grįžta su potvyniu, virš turgaus prekystalių sukasi kirai, o iš kepyklos ant kampo sklinda šviežios duonos kvapas. Devintą valandą aikštė pilna: žmonės perka kavą, vaikai važiuoja į mokyklą, mikroautobusas iškrauna dėžes su obuoliais ir kriaušėmis. Vėliau vėjas pasisuka ir virš salų kaupiasi debesys, tačiau kavinėje dirbama iki uždarymo. Atėjus vakarui, prieplaukoje po vieną įsijungia šviesos ir paskutinis dienos keltas išplaukia į šiaurinį krantą.',
  stop: 'Sustok',
  tooShort: 'Mums reikia šiek tiek daugiau kalbos. Prašome dar kartą perskaityti ištrauką.',
  multipleSpeakers: 'Išgirdome ne vieną balsą. Prašau įrašyti kur nors tyliai, savarankiškai.',
  poorAudio: 'Įrašas buvo per tylus arba per garsus. Bandykite dar kartą.',
  saved: 'Jūsų balso parašas išsaugotas.',
  processing: 'Kuriamas balso parašas…',
  recording: 'Įrašoma…',
  loading: 'Įkeliami balso nustatymai…',
  sources: 'Pagaminta iš',
  sourceRecording: 'Įrašas per {date}',
  sourceMeeting: '{title} {date}',
  removeSource: 'Pašalinti',
  delete: 'Ištrinkite balso parašą',
  deleteConfirm: 'Ištrinti balso parašą? Jo atviri pasiūlymai taip pat pašalinami.',
  deleted: 'Jūsų balso parašas ištrintas.',
  orgOff: 'Jūsų įmonė neįjungė balso parašų.',
  unavailable: 'Balso parašai šiuo metu nepasiekiami.',
  staleModel:
    'Patikros balsu buvo atnaujintos. Dar kartą nustatykite balso parašą, kad gautumėte pasiūlymų.',
  usage: 'Siūlė jūsų vardą {count} kartus',
  orgTitle: 'Balsas susirinkimuose',
  voicePassSwitch: 'Patikrinkite balsus susitikimuose',
  signaturesSwitch: 'Leisti balso parašus',
  unusedDays: 'Ištrinkite tiek daug dienų nenaudotus balso parašus',
  purgeWarning: 'Išjungus tai ištrinami {count} balso parašai.',
  enrolledCount: 'Balso parašai: {count}',
  auditTitle: 'Balso veikla',
  errorConsentRequired: 'Pirmiausia sutikite su balso parašo sąlygomis.',
  errorConsentChanged: 'Sąlygos pasikeitė. Perskaitykite juos dar kartą ir sutikite.',
  errorNotSubject: 'Galite nustatyti tik savo balso parašą.',
  errorNotEligible: 'Šiame susitikime neužtenka jūsų paties patvirtintos kalbos.',
  errorExpired: 'Šio įrašo galiojimo laikas baigėsi. Pradėkite iš naujo.',
  errorPurgeConfirmationRequired: 'Patvirtinkite, kad visi balso parašai bus ištrinti.',
};

const mt = {
  recognisedInMeetings: 'Leħnek: jingħaraf fil-laqgħat',

  title: 'Firma tal-vuċi',
  whatItIs:
    'Firma tal-vuċi tista’ tassenja ismek awtomatikament fil-laqgħat tal-kumpanija tiegħek meta t-tqabbil ikun qawwi, jew tissuġġerih meta jkun inqas ċert. Qatt ma tapprova jew tiftaħ xi ħaġa.',
  consentTitle: 'Qabel ma twaqqaf firma tal-vuċi',
  consentBody:
    'Poneva toħloq firma tal-vuċi minn reġistrazzjoni qasira jew diskors f’laqgħa li tikkonferma bħala tiegħek. Tinħażen ikkriptata u titqabbel biss fuq is-servers ta’ Poneva, fi ħdan il-kumpanija tiegħek. Tqabbil qawwi jassenja ismek awtomatikament b’għażla li tneħħi l-bidla; tqabbil inqas ċert jissuġġerih għall-konferma. Tista’ tħassar il-firma tiegħek fi kwalunkwe ħin, flimkien mas-suġġerimenti miftuħa tagħha.',
  consentCheckbox: 'Nifhem u rrid firma bil-vuċi.',
  agree: 'Naqbel',
  recordStart: 'Ibda rrekordja',
  passage:
    "Kull filgħodu l-port iqum bil-mod. Id-dgħajjes tas-sajd jirritornaw mal-marea, il-gawwi jdawru 'l fuq mill-monti tas-suq, u r-riħa ta' ħobż frisk tinfirex mill-forn fil-kantuniera. Sad-disgħa l-pjazza tkun mimlija: nies jixtru l-kafè, tfal fi triqithom lejn l-iskola, u vann li jħottu ċestuni tat-tuffieħ u tal-lanġas. Aktar tard ir-riħ idur u s-sħab jinġabar fuq il-gżejjer, iżda l-kafetterija tibqa okkupata sal-ħin tal-għeluq. Meta tasal filgħaxija, id-dwal tul il-moll jinxtegħlu wieħed wieħed, u l-aħħar lanċa tal-ġurnata titlaq lejn ix-xatt tat-tramuntana.",
  stop: 'Waqqaf',
  tooShort: 'Għandna bżonn ftit aktar diskors. Jekk jogħġbok aqra s-silta mill-ġdid.',
  multipleSpeakers:
    "Smajna aktar minn vuċi waħda. Jekk jogħġbok irreġistra x'imkien kwiet, waħdek.",
  poorAudio: "Ir-reġistrazzjoni kienet kwieta wisq jew qawwija wisq. Jekk jogħġbok erġa' pprova.",
  saved: 'Il-firma tal-vuċi tiegħek tiġi salvata.',
  processing: 'Il-ħolqien tal-firma tal-vuċi tiegħek...',
  recording: 'Qed jirrekordja…',
  loading: 'Qed jitgħabbew is-settings tal-vuċi…',
  sources: 'Magħmul minn',
  sourceRecording: 'Recording fuq {date}',
  sourceMeeting: '{title} fuq {date}',
  removeSource: 'Neħħi',
  delete: 'Ħassar il-firma tal-vuċi',
  deleteConfirm:
    'Tħassar il-firma tal-vuċi tiegħek? Is-suġġerimenti miftuħa tiegħu jitneħħew ukoll.',
  deleted: 'Il-firma tal-vuċi tiegħek titħassar.',
  orgOff: 'Il-kumpanija tiegħek ma xegħlitx il-firem bil-vuċi.',
  unavailable: 'Firem tal-vuċi mhumiex disponibbli bħalissa.',
  staleModel:
    "Il-kontrolli tal-vuċi ġew aġġornati. Erġa' waqqaf il-firma tal-vuċi tiegħek biex tkompli tirċievi suġġerimenti.",
  usage: 'Issuġġerit ismek {count} darbiet',
  orgTitle: 'Leħen fil-laqgħat',
  voicePassSwitch: 'Iċċekkja l-vuċijiet fil-laqgħat',
  signaturesSwitch: 'Ħalli firem bil-vuċi',
  unusedDays: 'Ħassar firem tal-vuċi li ma ntużawx għal dawn il-jiem',
  purgeWarning: "It-tifi ta' dan iħassar il-firem tal-vuċi {count}.",
  enrolledCount: 'Firem tal-vuċi: {count}',
  auditTitle: 'Attività tal-vuċi',
  errorConsentRequired: 'L-ewwel jaqbel mat-termini tal-firma tal-vuċi.',
  errorConsentChanged: 'It-termini nbidlu. Erġa’ aqrahom u jaqbel.',
  errorNotSubject: "Tista' biss twaqqaf il-firma tal-vuċi tiegħek stess.",
  errorNotEligible: "M'hemmx biżżejjed diskors ikkonfermat tiegħek stess f'din il-laqgħa.",
  errorExpired: 'Din ir-reġistrazzjoni skadiet. Ibda mill-ġdid.',
  errorPurgeConfirmationRequired: 'Ikkonferma li kull firma tal-vuċi se titħassar.',
};

const pl = {
  recognisedInMeetings: 'Twój głos: rozpoznawany na spotkaniach',

  title: 'Podpis głosowy',
  whatItIs:
    'Podpis głosowy może automatycznie przypisać Twoje imię na spotkaniach firmowych przy silnym dopasowaniu lub zasugerować je przy mniejszej pewności. Nigdy niczego nie zatwierdza ani nie odblokowuje.',
  consentTitle: 'Zanim skonfigurujesz podpis głosowy',
  consentBody:
    'Poneva tworzy podpis głosowy z krótkiego nagrania lub wypowiedzi ze spotkania, którą potwierdzisz jako własną. Jest przechowywany w postaci zaszyfrowanej i porównywany tylko na serwerach Poneva, w obrębie Twojej firmy. Silne dopasowania automatycznie przypisują Twoje imię z możliwością cofnięcia; mniej pewne proponują je do potwierdzenia. Możesz w każdej chwili usunąć podpis wraz z jego otwartymi sugestiami.',
  consentCheckbox: 'Rozumiem i chcę podpisu głosowego.',
  agree: 'Zgadzam się',
  recordStart: 'Rozpocznij nagrywanie',
  passage:
    'Każdego ranka port budzi się powoli. Łodzie rybackie wracają z przypływem, mewy krążą nad straganami, a z piekarni na rogu unosi się zapach świeżego chleba. O dziewiątej plac jest pełen: ludzie kupujący kawę, dzieci jadące do szkoły i furgonetka rozładowująca skrzynki z jabłkami i gruszkami. Później wiatr się zmienia i nad wyspami gromadzą się chmury, ale kawiarnia pozostaje zajęta aż do zamknięcia. Kiedy nadchodzi wieczór, światła na molo włączają się jedno po drugim i ostatni prom tego dnia odpływa na północny brzeg.',
  stop: 'Zatrzymywać się',
  tooShort: 'Potrzebujemy trochę więcej przemówienia. Proszę przeczytać fragment jeszcze raz.',
  multipleSpeakers:
    'Słyszeliśmy więcej niż jeden głos. Proszę nagrywać w cichym miejscu, w samotności.',
  poorAudio: 'Nagranie było za ciche lub za głośne. Spróbuj ponownie.',
  saved: 'Twój podpis głosowy został zapisany.',
  processing: 'Tworzenie podpisu głosowego…',
  recording: 'Nagrywanie…',
  loading: 'Wczytywanie ustawień głosu…',
  sources: 'Wykonane z',
  sourceRecording: 'Nagranie na {date}',
  sourceMeeting: '{title} na {date}',
  removeSource: 'Usunąć',
  delete: 'Usuń podpis głosowy',
  deleteConfirm: 'Usunąć swój podpis głosowy? Jego otwarte sugestie również zostaną usunięte.',
  deleted: 'Twój podpis głosowy został usunięty.',
  orgOff: 'Twoja firma nie włączyła podpisów głosowych.',
  unavailable: 'Podpisy głosowe nie są obecnie dostępne.',
  staleModel:
    'Zaktualizowano kontrole głosowe. Aby nadal otrzymywać sugestie, skonfiguruj swój podpis głosowy ponownie.',
  usage: 'Sugerowano Twoje imię {count} razy',
  orgTitle: 'Głos na spotkaniach',
  voicePassSwitch: 'Sprawdzaj głosy na spotkaniach',
  signaturesSwitch: 'Zezwalaj na podpisy głosowe',
  unusedDays: 'Usuń podpisy głosowe nieużywane przez tyle dni',
  purgeWarning: 'Wyłączenie tej opcji powoduje usunięcie podpisów głosowych {count}.',
  enrolledCount: 'Podpisy głosowe: {count}',
  auditTitle: 'Aktywność głosowa',
  errorConsentRequired: 'Najpierw zaakceptuj warunki podpisu głosowego.',
  errorConsentChanged: 'Warunki uległy zmianie. Przeczytaj je jeszcze raz i zgódź się.',
  errorNotSubject: 'Możesz skonfigurować tylko własny podpis głosowy.',
  errorNotEligible: 'Na tym spotkaniu nie wystarczy Pańskich potwierdzonych przemówień.',
  errorExpired: 'To nagranie straciło ważność. Zacznij od nowa.',
  errorPurgeConfirmationRequired: 'Potwierdź, że każdy podpis głosowy zostanie usunięty.',
};

const pt = {
  recognisedInMeetings: 'A sua voz: reconhecida em reuniões',

  title: 'Assinatura de voz',
  whatItIs:
    'Uma assinatura de voz pode atribuir automaticamente o seu nome nas reuniões da empresa quando a correspondência é forte, ou sugeri-lo quando é menos certa. Nunca aprova nem desbloqueia nada.',
  consentTitle: 'Antes de configurar uma assinatura de voz',
  consentBody:
    'A Poneva cria uma assinatura de voz a partir de uma gravação curta ou de falas numa reunião que confirme como suas. É guardada encriptada e comparada apenas nos servidores da Poneva, dentro da sua empresa. As correspondências fortes atribuem o seu nome automaticamente, com opção de anular; as menos certas sugerem-no para confirmação. Pode eliminar a assinatura a qualquer momento, juntamente com as sugestões em aberto.',
  consentCheckbox: 'Eu entendo e quero uma assinatura de voz.',
  agree: 'Concordo',
  recordStart: 'Comece a gravar',
  passage:
    'Todas as manhãs o porto acorda lentamente. Os barcos de pesca regressam com a maré, as gaivotas voam sobre as bancas do mercado e o cheiro a pão fresco exala da padaria da esquina. Às nove horas a praça está cheia: gente comprando café, crianças a caminho da escola e uma van descarregando caixotes de maçãs e peras. Mais tarde o vento muda e as nuvens juntam-se sobre as ilhas, mas o café permanece movimentado até à hora de fechar. Quando chega a noite, as luzes ao longo do cais acendem uma a uma e a última balsa do dia parte para a costa norte.',
  stop: 'Parar',
  tooShort: 'Precisamos de um pouco mais de discurso. Por favor, leia a passagem novamente.',
  multipleSpeakers: 'Ouvimos mais de uma voz. Por favor, grave em algum lugar tranquilo, sozinho.',
  poorAudio: 'A gravação estava muito baixa ou muito alta. Por favor, tente novamente.',
  saved: 'Sua assinatura de voz é salva.',
  processing: 'Criando sua assinatura de voz…',
  recording: 'A gravar…',
  loading: 'Carregando configurações de voz…',
  sources: 'Feito de',
  sourceRecording: 'Uma gravação no {date}',
  sourceMeeting: '{title} em {date}',
  removeSource: 'Remover',
  delete: 'Excluir assinatura de voz',
  deleteConfirm: 'Excluir sua assinatura de voz? Suas sugestões abertas também foram removidas.',
  deleted: 'Sua assinatura de voz foi excluída.',
  orgOff: 'Sua empresa não ativou assinaturas de voz.',
  unavailable: 'As assinaturas de voz não estão disponíveis no momento.',
  staleModel:
    'As verificações de voz foram atualizadas. Configure sua assinatura de voz novamente para continuar recebendo sugestões.',
  usage: 'Sugeriu seu nome {count} vezes',
  orgTitle: 'Voz em reuniões',
  voicePassSwitch: 'Verifique vozes em reuniões',
  signaturesSwitch: 'Permitir assinaturas de voz',
  unusedDays: 'Exclua assinaturas de voz não utilizadas por tantos dias',
  purgeWarning: 'Desativar isso exclui as assinaturas de voz {count}.',
  enrolledCount: 'Assinaturas de voz: {count}',
  auditTitle: 'Atividade de voz',
  errorConsentRequired: 'Concorde primeiro com os termos da assinatura de voz.',
  errorConsentChanged: 'Os termos mudaram. Leia-os novamente e concorde.',
  errorNotSubject: 'Você só pode configurar sua própria assinatura de voz.',
  errorNotEligible: 'Não há discurso confirmado suficiente nesta reunião.',
  errorExpired: 'Esta gravação expirou. Comece de novo.',
  errorPurgeConfirmationRequired: 'Confirme que todas as assinaturas de voz serão excluídas.',
};

const ro = {
  recognisedInMeetings: 'Vocea ta: recunoscută în întâlniri',

  title: 'Semnătura vocală',
  whatItIs:
    'O semnătură vocală poate atribui automat numele dvs. în ședințele companiei când potrivirea este puternică sau îl poate sugera când este mai puțin sigură. Nu aprobă și nu deblochează niciodată nimic.',
  consentTitle: 'Înainte de a configura o semnătură vocală',
  consentBody:
    'Poneva creează o semnătură vocală dintr-o înregistrare scurtă sau din intervenții la ședință pe care le confirmați ca fiind ale dvs. Este stocată criptat și comparată numai pe serverele Poneva, în cadrul companiei dvs. Potrivirile puternice atribuie numele automat, cu opțiune de anulare; cele mai puțin sigure îl propun pentru confirmare. Puteți șterge semnătura oricând, împreună cu sugestiile sale deschise.',
  consentCheckbox: 'Înțeleg și vreau o semnătură vocală.',
  agree: 'Sunt de acord',
  recordStart: 'Începeți înregistrarea',
  passage:
    'În fiecare dimineață portul se trezește încet. Bărcile de pescuit se întorc odată cu valul, pescărușii se rotesc deasupra tarabelor din piață, iar mirosul de pâine proaspătă plutește din brutăria din colț. Până la ora nouă piața este plină: oameni care cumpără cafea, copii în drum spre școală și o dubă care descarcă lăzi cu mere și pere. Mai târziu vântul se întoarce și norii se adună peste insule, dar cafeneaua rămâne ocupată până la ora închiderii. Când vine seara, luminile de-a lungul digului se aprind una câte una, iar ultimul feribot al zilei pleacă spre malul de nord.',
  stop: 'Stop',
  tooShort: 'Mai avem nevoie de un pic de discurs. Vă rugăm să citiți din nou pasajul.',
  multipleSpeakers:
    'Am auzit mai mult de o voce. Vă rugăm să înregistrați într-un loc liniștit, pe cont propriu.',
  poorAudio: 'Înregistrarea a fost prea silențioasă sau prea tare. Vă rugăm să încercați din nou.',
  saved: 'Semnătura dvs. vocală este salvată.',
  processing: 'Se creează semnătura vocală...',
  recording: 'Se înregistrează…',
  loading: 'Se încarcă setările vocale…',
  sources: 'Fabricat din',
  sourceRecording: 'O înregistrare pe {date}',
  sourceMeeting: '{title} pe {date}',
  removeSource: 'Elimina',
  delete: 'Ștergeți semnătura vocală',
  deleteConfirm: 'Ștergeți semnătura vocală? Sugestiile sale deschise sunt și ele eliminate.',
  deleted: 'Semnătura dvs. vocală este ștearsă.',
  orgOff: 'Compania dvs. nu a activat semnăturile vocale.',
  unavailable: 'Semnăturile vocale nu sunt disponibile momentan.',
  staleModel:
    'Verificările vocale au fost actualizate. Configurați din nou semnătura vocală pentru a primi sugestii în continuare.',
  usage: 'Ți-a sugerat numele {count} de ori',
  orgTitle: 'Voce în întâlniri',
  voicePassSwitch: 'Verificați vocile în întâlniri',
  signaturesSwitch: 'Permite semnături vocale',
  unusedDays: 'Ștergeți semnăturile vocale neutilizate pentru atâtea zile',
  purgeWarning: 'Dezactivarea acestei opțiuni șterge semnăturile vocale {count}.',
  enrolledCount: 'Semnături vocale: {count}',
  auditTitle: 'Activitatea vocală',
  errorConsentRequired: 'Acceptați mai întâi termenii semnăturii vocale.',
  errorConsentChanged: 'Termenii s-au schimbat. Citiți-le din nou și fiți de acord.',
  errorNotSubject: 'Puteți configura doar propria dvs. semnătură vocală.',
  errorNotEligible: 'Nu există suficient discursul dumneavoastră confirmat în această întâlnire.',
  errorExpired: 'Această înregistrare a expirat. Începe din nou.',
  errorPurgeConfirmationRequired: 'Confirmați că fiecare semnătură vocală va fi ștearsă.',
};

const sk = {
  recognisedInMeetings: 'Váš hlas: rozpoznávaný na stretnutiach',

  title: 'Hlasový podpis',
  whatItIs:
    'Hlasový podpis vás môže pri silnej zhode automaticky pomenovať na firemných stretnutiach alebo pri nižšej istote navrhnúť vaše meno. Nikdy nič neschvaľuje ani neodomyká.',
  consentTitle: 'Pred nastavením hlasového podpisu',
  consentBody:
    'Poneva vytvorí hlasový podpis z krátkej nahrávky alebo reči zo stretnutia, ktorú potvrdíte ako vlastnú. Ukladá sa zašifrovaný a porovnáva sa iba na serveroch Poneva v rámci vašej firmy. Silné zhody automaticky priradia vaše meno s možnosťou vrátenia; menej isté ho navrhnú na potvrdenie. Podpis môžete kedykoľvek vymazať spolu s jeho otvorenými návrhmi.',
  consentCheckbox: 'Rozumiem a chcem hlasový podpis.',
  agree: 'súhlasím',
  recordStart: 'Spustite nahrávanie',
  passage:
    'Každé ráno sa prístav pomaly prebúdza. Rybárske člny sa vracajú s prílivom, nad stánkami na trhu krúžia čajky a z pekárne na rohu sa nesie vôňa čerstvého chleba. O deviatej je námestie plné: ľudia kupujú kávu, deti na ceste do školy a dodávka vykladá debny s jablkami a hruškami. Neskôr sa vietor otočí a nad ostrovmi sa zhromažďujú mraky, ale kaviareň je rušná až do záverečnej hodiny. Keď príde večer, svetlá na móle sa jedno po druhom rozsvietia a posledný trajekt dňa odchádza na severný breh.',
  stop: 'Stop',
  tooShort: 'Potrebujeme trochu viac reči. Prečítajte si pasáž ešte raz.',
  multipleSpeakers: 'Počuli sme viac ako jeden hlas. Prosím, nahrávajte niekde v tichu, sami.',
  poorAudio: 'Nahrávanie bolo príliš tiché alebo príliš hlasné. Skúste to znova.',
  saved: 'Váš hlasový podpis sa uloží.',
  processing: 'Vytvára sa váš hlasový podpis…',
  recording: 'Nahrávanie…',
  loading: 'Načítavajú sa nastavenia hlasu…',
  sources: 'Vyrobené z',
  sourceRecording: 'Záznam na {date}',
  sourceMeeting: '{title} na {date}',
  removeSource: 'Odstrániť',
  delete: 'Odstrániť hlasový podpis',
  deleteConfirm: 'Chcete odstrániť svoj hlasový podpis? Jeho otvorené návrhy sú tiež odstránené.',
  deleted: 'Váš hlasový podpis je odstránený.',
  orgOff: 'Vaša spoločnosť nemá zapnuté hlasové podpisy.',
  unavailable: 'Hlasové podpisy momentálne nie sú k dispozícii.',
  staleModel:
    'Hlasové kontroly boli aktualizované. Ak chcete naďalej dostávať návrhy, nastavte si hlasový podpis znova.',
  usage: 'Navrhnuté vaše meno {count}-krát',
  orgTitle: 'Hlas na stretnutiach',
  voicePassSwitch: 'Skontrolujte hlasy na stretnutiach',
  signaturesSwitch: 'Povoliť hlasové podpisy',
  unusedDays: 'Odstráňte hlasové podpisy, ktoré sa toľko dní nepoužívali',
  purgeWarning: 'Vypnutím tejto možnosti sa odstránia hlasové podpisy {count}.',
  enrolledCount: 'Hlasové podpisy: {count}',
  auditTitle: 'Hlasová aktivita',
  errorConsentRequired: 'Najprv súhlaste s podmienkami hlasového podpisu.',
  errorConsentChanged: 'Podmienky sa zmenili. Prečítajte si ich ešte raz a súhlaste.',
  errorNotSubject: 'Môžete nastaviť iba svoj vlastný hlasový podpis.',
  errorNotEligible: 'Na tomto stretnutí je málo vašich vlastných potvrdených prejavov.',
  errorExpired: 'Platnosť tejto nahrávky vypršala. Začnite znova.',
  errorPurgeConfirmationRequired: 'Potvrďte, že každý hlasový podpis bude odstránený.',
};

const sl = {
  recognisedInMeetings: 'Vaš glas: prepoznan na sestankih',

  title: 'Glasovni podpis',
  whatItIs:
    'Glasovni podpis vas lahko pri močnem ujemanju samodejno poimenuje na sestankih podjetja ali pri manjši gotovosti predlaga vaše ime. Nikoli ničesar ne odobri ali odklene.',
  consentTitle: 'Preden nastavite glasovni podpis',
  consentBody:
    'Poneva ustvari glasovni podpis iz kratkega posnetka ali govora na sestanku, ki ga potrdite kot svojega. Shranjen je šifrirano in se primerja samo na strežnikih Poneva znotraj vašega podjetja. Močna ujemanja samodejno dodelijo vaše ime z možnostjo razveljavitve; manj gotova ga predlagajo v potrditev. Podpis lahko kadar koli izbrišete skupaj z njegovimi odprtimi predlogi.',
  consentCheckbox: 'Razumem in želim glasovni podpis.',
  agree: 'se strinjam',
  recordStart: 'Začnite snemati',
  passage:
    'Vsako jutro se pristanišče počasi prebuja. S plimo se vračajo ribiški čolni, nad tržnicami krožijo galebi, iz pekarne na vogalu pa diši po svežem kruhu. Ob deveti uri je trg poln: ljudje, ki kupujejo kavo, otroci na poti v šolo, kombi, ki raztovarja zaboje jabolk in hrušk. Kasneje se veter obrne in nad otoke se zgrnejo oblaki, vendar je kavarna zasedena do zaprtja. Ko se zvečeri, se ena za drugo prižgejo luči ob pomolu in zadnji trajekt dneva odpluje proti severni obali.',
  stop: 'Stop',
  tooShort: 'Potrebujemo malo več govora. Prosim, še enkrat preberite odlomek.',
  multipleSpeakers: 'Slišali smo več kot en glas. Prosimo, snemajte nekje na tihem mestu, sami.',
  poorAudio: 'Posnetek je bil pretih ali preglasen. prosim poskusite ponovno',
  saved: 'Vaš glasovni podpis je shranjen.',
  processing: 'Ustvarjanje vašega glasovnega podpisa ...',
  recording: 'Snemanje…',
  loading: 'Nalaganje glasovnih nastavitev…',
  sources: 'Izdelano iz',
  sourceRecording: 'Posnetek na {date}',
  sourceMeeting: '{title} na {date}',
  removeSource: 'Odstrani',
  delete: 'Izbriši glasovni podpis',
  deleteConfirm:
    'Želite izbrisati svoj glasovni podpis? Odstranjeni so tudi njegovi odprti predlogi.',
  deleted: 'Vaš glasovni podpis je izbrisan.',
  orgOff: 'Vaše podjetje ni vklopilo glasovnih podpisov.',
  unavailable: 'Glasovni podpisi trenutno niso na voljo.',
  staleModel:
    'Glasovna preverjanja so bila posodobljena. Znova nastavite svoj glasovni podpis, da boste še naprej prejemali predloge.',
  usage: 'Vaše ime je bilo predlagano {count}-krat',
  orgTitle: 'Glas na sestankih',
  voicePassSwitch: 'Preverite glasove na sestankih',
  signaturesSwitch: 'Dovoli glasovne podpise',
  unusedDays: 'Izbrišite glasovne podpise, ki niso bili uporabljeni toliko dni',
  purgeWarning: 'Če to izklopite, izbrišete glasovne podpise {count}.',
  enrolledCount: 'Glasovni podpisi: {count}',
  auditTitle: 'Glasovna dejavnost',
  errorConsentRequired: 'Najprej se strinjajte s pogoji glasovnega podpisa.',
  errorConsentChanged: 'Pogoji so se spremenili. Preberite jih še enkrat in se strinjate.',
  errorNotSubject: 'Nastavite lahko le lasten glasovni podpis.',
  errorNotEligible: 'Na tem sestanku ni dovolj vašega potrjenega govora.',
  errorExpired: 'Ta posnetek je potekel. Začni znova.',
  errorPurgeConfirmationRequired: 'Potrdite, da bo vsak glasovni podpis izbrisan.',
};

const es = {
  recognisedInMeetings: 'Tu voz: reconocida en reuniones',

  title: 'Firma de voz',
  whatItIs:
    'Una firma de voz puede asignar tu nombre automáticamente en las reuniones de tu empresa si la coincidencia es fuerte, o sugerirlo si es menos segura. Nunca aprueba ni desbloquea nada.',
  consentTitle: 'Antes de configurar una firma de voz',
  consentBody:
    'Poneva crea una firma de voz a partir de una grabación breve o de intervenciones en reuniones que confirmes como tuyas. Se guarda cifrada y se compara solo en los servidores de Poneva, dentro de tu empresa. Las coincidencias fuertes asignan tu nombre automáticamente, con opción de deshacer; las menos seguras lo proponen para confirmación. Puedes eliminar la firma en cualquier momento, junto con sus sugerencias pendientes.',
  consentCheckbox: 'Entiendo y quiero una firma de voz.',
  agree: 'Estoy de acuerdo',
  recordStart: 'Empezar a grabar',
  passage:
    'Cada mañana el puerto se despierta lentamente. Los barcos de pesca regresan con la marea, las gaviotas vuelan sobre los puestos del mercado y el olor a pan recién hecho llega desde la panadería de la esquina. A las nueve en punto la plaza está llena: gente comprando café, niños de camino a la escuela y una furgoneta descargando cajas de manzanas y peras. Más tarde el viento cambia y las nubes se acumulan sobre las islas, pero el café permanece lleno hasta la hora de cerrar. Al anochecer, las luces del muelle se encienden una a una y el último ferry del día parte hacia la costa norte.',
  stop: 'Detener',
  tooShort: 'Necesitamos un poco más de discurso. Por favor lea el pasaje nuevamente.',
  multipleSpeakers: 'Escuchamos más de una voz. Graba por tu cuenta en un lugar tranquilo.',
  poorAudio: 'La grabación fue demasiado baja o demasiado alta. Por favor inténtalo de nuevo.',
  saved: 'Su firma de voz se guarda.',
  processing: 'Creando tu firma de voz...',
  recording: 'Grabando…',
  loading: 'Cargando la configuración de voz…',
  sources: 'hecho de',
  sourceRecording: 'Una grabación en {date}',
  sourceMeeting: '{title} en {date}',
  removeSource: 'Eliminar',
  delete: 'Eliminar firma de voz',
  deleteConfirm: '¿Eliminar tu firma de voz? También se eliminan sus sugerencias abiertas.',
  deleted: 'Se elimina tu firma de voz.',
  orgOff: 'Su empresa no ha activado las firmas de voz.',
  unavailable: 'Las firmas de voz no están disponibles en este momento.',
  staleModel:
    'Se actualizaron las comprobaciones de voz. Configura tu firma de voz nuevamente para seguir recibiendo sugerencias.',
  usage: 'Sugerió su nombre {count} veces',
  orgTitle: 'Voz en reuniones',
  voicePassSwitch: 'Comprobar voces en reuniones',
  signaturesSwitch: 'Permitir firmas de voz',
  unusedDays: 'Eliminar firmas de voz no utilizadas durante tantos días',
  purgeWarning: 'Desactivar esto elimina las firmas de voz {count}.',
  enrolledCount: 'Firmas de voz: {count}',
  auditTitle: 'Actividad de voz',
  errorConsentRequired: 'Primero acepte los términos de la firma de voz.',
  errorConsentChanged: 'Los términos cambiaron. Léelos nuevamente y acepta.',
  errorNotSubject: 'Sólo puedes configurar tu propia firma de voz.',
  errorNotEligible: 'No hay suficiente discurso confirmado en esta reunión.',
  errorExpired: 'Esta grabación expiró. Empezar de nuevo.',
  errorPurgeConfirmationRequired: 'Confirme que se eliminarán todas las firmas de voz.',
};

const sv = {
  recognisedInMeetings: 'Din röst: känns igen i möten',

  title: 'Röstsignatur',
  whatItIs:
    'En röstsignatur kan namnge dig automatiskt i företagets möten vid en stark matchning, eller föreslå ditt namn när matchningen är mindre säker. Den godkänner eller låser aldrig upp något.',
  consentTitle: 'Innan du ställer in en röstsignatur',
  consentBody:
    'Poneva skapar en röstsignatur från en kort inspelning eller mötestal som du bekräftar är ditt eget. Den lagras krypterat och jämförs bara på Ponevas egna servrar, inom ditt företag. Starka matchningar lägger till ditt namn automatiskt med möjlighet att ångra; mindre säkra matchningar föreslår det för bekräftelse. Du kan ta bort signaturen när som helst, tillsammans med dess öppna förslag.',
  consentCheckbox: 'Jag förstår och vill ha en röstsignatur.',
  agree: 'Jag håller med',
  recordStart: 'Börja spela in',
  passage:
    'Varje morgon vaknar hamnen långsamt. Fiskebåtar återvänder med tidvattnet, måsar cirklar ovanför marknadsstånden och doften av färskt bröd driver från bageriet på hörnet. Vid niotiden är torget fullt: folk köper kaffe, barn på väg till skolan och en skåpbil som lastar av lådor med äpplen och päron. Senare vänder vinden och molnen samlas över öarna, men caféet är upptaget fram till stängningstid. När kvällen kommer tänds ljusen längs piren en efter en och dagens sista färja går till norra stranden.',
  stop: 'Stopp',
  tooShort: 'Vi behöver lite mer tal. Läs avsnittet igen.',
  multipleSpeakers: 'Vi hörde mer än en röst. Vänligen spela in någonstans tyst, på egen hand.',
  poorAudio: 'Inspelningen var för tyst eller för hög. Försök igen.',
  saved: 'Din röstsignatur sparas.',
  processing: 'Skapar din röstsignatur...',
  recording: 'Spelar in…',
  loading: 'Läser in röstinställningar…',
  sources: 'Tillverkad av',
  sourceRecording: 'En inspelning på {date}',
  sourceMeeting: '{title} på {date}',
  removeSource: 'Ta bort',
  delete: 'Ta bort röstsignatur',
  deleteConfirm: 'Vill du ta bort din röstsignatur? Dess öppna förslag tas också bort.',
  deleted: 'Din röstsignatur raderas.',
  orgOff: 'Ditt företag har inte aktiverat röstsignaturer.',
  unavailable: 'Röstsignaturer är inte tillgängliga just nu.',
  staleModel:
    'Röstkontroller har uppdaterats. Konfigurera din röstsignatur igen för att fortsätta få förslag.',
  usage: 'Föreslagit ditt namn {count} gånger',
  orgTitle: 'Röst i möten',
  voicePassSwitch: 'Kontrollera röster i möten',
  signaturesSwitch: 'Tillåt röstsignaturer',
  unusedDays: 'Ta bort röstsignaturer som inte har använts under så många dagar',
  purgeWarning: 'Om du stänger av detta raderas {count} röstsignaturer.',
  enrolledCount: 'Röstsignaturer: {count}',
  auditTitle: 'Röstaktivitet',
  errorConsentRequired: 'Godkänn först villkoren för röstsignaturen.',
  errorConsentChanged: 'Villkoren ändrades. Läs dem igen och håll med.',
  errorNotSubject: 'Du kan bara ställa in din egen röstsignatur.',
  errorNotEligible: 'Det finns inte tillräckligt med ditt eget bekräftade tal i detta möte.',
  errorExpired: 'Denna inspelning har upphört att gälla. Börja igen.',
  errorPurgeConfirmationRequired: 'Bekräfta att varje röstsignatur kommer att raderas.',
};

const voiceSignatureCopy: NamespaceCatalog<VoiceSignatureCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default voiceSignatureCopy;
